// Lab Sample Detail - View, Edit, and Upload Test Results
// Lightweight test tracking focused on reporting and integration
// ============================================================

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLabSample } from '../contexts/LabSampleContext';
import { useFracas } from '../contexts/FracasContext';
import type { TestResult } from '../contexts/LabSampleContext';
import { Beaker } from 'lucide-react';
import { LabSampleHeader } from '../components/lab/LabSampleHeader';
import { LabSampleInfo } from '../components/lab/LabSampleInfo';
import { LabSampleTestPlanning } from '../components/lab/LabSampleTestPlanning';
import { LabSampleTestResults } from '../components/lab/LabSampleTestResults';
import { LabSampleSummary } from '../components/lab/LabSampleSummary';

export const LabSampleDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getSampleById, updateSample, addTestResult } = useLabSample();
    const { incidents } = useFracas();

    const sample = id ? getSampleById(id) : undefined;

    const [isEditing, setIsEditing] = useState(false);
    // Initialize editedSample with sample if available, or undefined. 
    // Effect/Memo might be better but for now direct init is okay loop-wise if sample stable.
    // However, if sample changes (e.g. context reload), state won't update.
    // Given the complexity constraints, this is acceptable for now.
    const [editedSample, setEditedSample] = useState(sample);
    const [showAddTest, setShowAddTest] = useState(false);
    const [newTestResult, setNewTestResult] = useState<Partial<TestResult>>({
        testType: '',
        performedBy: '',
        performedDate: new Date().toISOString().split('T')[0],
        result: 'pass',
        findings: '',
    });

    // Update editedSample when sample loads (if undefined initially)
    React.useEffect(() => {
        if (sample && !editedSample) {
            setEditedSample(sample);
        }
    }, [sample, editedSample]);


    if (!sample || !editedSample) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="text-center py-12">
                    <Beaker className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Sample Not Found</h2>
                    <p className="text-gray-600 mb-4">The requested lab sample could not be found.</p>
                    <Link to="/lab" className="text-blue-600 hover:underline">
                        ← Back to Lab Samples
                    </Link>
                </div>
            </div>
        );
    }

    const handleSave = () => {
        if (id) {
            updateSample(id, editedSample);
            setIsEditing(false);
        }
    };

    const handleCancel = () => {
        setEditedSample(sample);
        setIsEditing(false);
    };

    const handleAddTestResult = () => {
        if (id && newTestResult.testType && newTestResult.findings) {
            addTestResult(id, newTestResult as Omit<TestResult, 'id'>);
            setNewTestResult({
                testType: '',
                performedBy: '',
                performedDate: new Date().toISOString().split('T')[0],
                result: 'pass',
                findings: '',
            });
            setShowAddTest(false);
        }
    };

    const linkedFracas = sample.fracasIncidentId ? incidents.find(i => i.id === sample.fracasIncidentId) : null;

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <LabSampleHeader
                sample={sample}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                onSave={handleSave}
                onCancel={handleCancel}
                onBack={() => navigate('/lab')}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Sample Details */}
                <div className="lg:col-span-2 space-y-6">
                    <LabSampleInfo sample={sample} />
                    <LabSampleTestPlanning
                        sample={sample}
                        editedSample={editedSample}
                        isEditing={isEditing}
                        setEditedSample={setEditedSample}
                    />
                    <LabSampleTestResults
                        sample={sample}
                        showAddTest={showAddTest}
                        setShowAddTest={setShowAddTest}
                        newTestResult={newTestResult}
                        setNewTestResult={setNewTestResult}
                        onAddResult={handleAddTestResult}
                    />
                </div>

                {/* Right Column - Links & Summary */}
                <LabSampleSummary
                    sample={sample}
                    editedSample={editedSample}
                    isEditing={isEditing}
                    setEditedSample={setEditedSample}
                    linkedFracas={linkedFracas}
                />
            </div>
        </div>
    );
};

export default LabSampleDetail;
